// Meta Pixel (TAS-338).
//
// Why this exists: until now nothing in the repo referenced `fbq`, so Meta
// could not see a single conversion on tasted.co.za. Any ad spend was paying
// Meta to optimise against a signal it did not have.
//
// Three rules this module holds to:
//
//  1. It is inert without `VITE_META_PIXEL_ID`. No env var, no script tag, no
//     network call. That is what lets this ship before the ad account exists.
//  2. It never sends PII. Meta's Conversions API wants hashed email; that
//     hashing belongs server-side (see supabase/functions/meta-capi), not in a
//     browser bundle. The browser side sends behaviour only, matching the
//     PII-free rule the Mixpanel client already follows (TAS-73).
//  3. It requires EXPLICIT consent. Mixpanel runs on implied consent with a
//     visible notice (TAS-252) and that stays as it is — but an advertising
//     pixel is a different category from product analytics, and "no answer yet"
//     is not a yes. Nothing loads until the visitor presses Accept.
//     (Codex pair-review, 2026-08-29.)
//
// Events are mapped from the app's OWN event names rather than sprinkling
// fbq() calls through the components. One map, one place to audit, and no way
// for a call site to send Meta something Mixpanel never saw.

// Plain `import.meta.env.VITE_…`, no optional chaining: Vite substitutes this at
// BUILD time by matching that literal text, and `import.meta.env?.VITE_…` is not
// guaranteed to match.
//
// Either way, the check that actually proves this works is grepping the BUILT
// bundle for the id — not a test. On the first deploy the id was missing from
// the bundle because the env var had been created empty, and nothing else
// surfaced it: `vercel env ls` prints "Encrypted" for an empty value, and
// vi.stubEnv in tests writes to the runtime object rather than the build-time
// replacement, so every test still passed. (2026-08-29)
const PIXEL_ID = import.meta.env.VITE_META_PIXEL_ID;

// Our event name -> [Meta standard event, how to build its params].
// Anything not in this map is deliberately not forwarded: Meta's optimiser
// works off a small vocabulary, and flooding it with 80 game events makes the
// signal worse, not better.
const EVENT_MAP = {
  'Page View': ['PageView', () => ({})],
  'Box Viewed': ['ViewContent', (p) => ({ content_name: p.box, content_type: 'product' })],
  'Boxes Section Viewed': ['ViewContent', () => ({ content_type: 'product_group' })],
  'Box Added to Cart': ['AddToCart', (p) => ({ content_name: p.box, value: num(p.value), currency: 'ZAR' })],
  'Checkout Started': ['InitiateCheckout', (p) => ({ value: num(p.value), currency: 'ZAR', num_items: num(p.item_count) || 1 })],
  'Order Placed': ['Purchase', (p) => ({ value: num(p.value), currency: 'ZAR', content_name: p.box, num_items: num(p.item_count) || 1 })],
  'Quiz Email Submitted': ['Lead', () => ({ content_name: 'wine-quiz' })],
  'Quiz Completed': ['CompleteRegistration', () => ({ content_name: 'wine-quiz' })],
  'waitlist_signup_success': ['Lead', () => ({ content_name: 'waitlist' })],
};

const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

let _loaded = false;
let _granted = false;

/** True when a pixel id is configured, consent was given, and fbevents loaded. */
export const pixelActive = () => Boolean(PIXEL_ID) && _granted && _loaded;

/**
 * Query parameters that must never reach Meta.
 *
 * Meta's standard events carry the current document URL, and we send people to
 * URLs that carry personal data: the recap prize email links land on
 * `/?src=recap-prize&discount=…&email=<their address>`, and the game handoff
 * adds `mp_id`. A PageView from either would hand Meta an email address and an
 * analytics identity in the clear. `autoConfig:false` does not help here — it
 * stops Meta's own listeners, not the URL on the event.
 *
 * So the URL is rewritten before anything loads. history.replaceState keeps the
 * page and the back button intact; the app's own readers (attribution, prize
 * claim) run off the values captured before this point.
 * (Codex pair-review, 2026-08-29.)
 */
// Only what is genuinely personal, and only what nothing else needs from the URL.
//
// The first version of this also stripped `discount`, `token` and `claim`. That
// broke real flows, because those are FUNCTIONAL parameters, not PII: the recap
// prize link carries `?discount=`, which CommerceOverlay and Checkout read to
// apply the prize, and `?claim=` is what PrizeRedeem opens on. A promo code and
// an opaque claim id are not personal data; an email address is.
const SENSITIVE_PARAMS = ['email', 'mp_id'];

// Scrubbing runs at module load, BEFORE React renders — so any component that
// reads one of these from the query string would find it already gone. That bug
// silently disabled the email prefill and the cross-origin identity handoff.
// The values are therefore kept here for the app to read.
const _stashed = Object.create(null);

/** A parameter that was removed from the URL, or null. */
export const getScrubbedParam = (key) => _stashed[key] ?? null;

/**
 * Remove personal parameters from the visible URL before the pixel loads.
 *
 * Meta's standard events carry the current document URL, and the recap prize
 * link lands on `/?src=recap-prize&discount=…&email=<their address>`. Without
 * this the very first PageView hands Meta a customer's email in the clear.
 * `autoConfig:false` does not help — it stops Meta's own listeners, not the URL
 * on the event.
 *
 * history.replaceState keeps the page and the back button intact.
 */
export function scrubSensitiveParamsFromUrl() {
  if (typeof window === 'undefined' || !window.history?.replaceState) return;
  try {
    const url = new URL(window.location.href);
    let changed = false;
    for (const key of SENSITIVE_PARAMS) {
      const value = url.searchParams.get(key);
      if (value !== null) {
        _stashed[key] = value; // keep it for the app, take it off the URL
        url.searchParams.delete(key);
        changed = true;
      }
    }
    if (changed) window.history.replaceState(window.history.state, '', url.toString());
  } catch { /* a URL we cannot parse is a URL we cannot leak from either */ }
}

/**
 * Inject Meta's fbevents.js. Idempotent, and a no-op without a pixel id.
 *
 * `autoConfig: false` stops Meta's script from attaching its own listeners to
 * every button and form on the page — that behaviour is what silently sends
 * form field contents to Meta. We send exactly what EVENT_MAP says and nothing
 * else.
 */
export function initMetaPixel() {
  if (!PIXEL_ID || _loaded || !_granted) return;
  if (typeof window === 'undefined' || typeof document === 'undefined') return;

  // Never load with an email address still on the URL — the very first
  // PageView would carry it to Meta.
  scrubSensitiveParamsFromUrl();

  try {
    /* eslint-disable */
    !(function (f, b, e, v, n, t, s) {
      if (f.fbq) return;
      n = f.fbq = function () {
        n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
      };
      if (!f._fbq) f._fbq = n;
      n.push = n; n.loaded = true; n.version = '2.0'; n.queue = [];
      t = b.createElement(e); t.async = true; t.src = v;
      s = b.getElementsByTagName(e)[0]; s.parentNode.insertBefore(t, s);
    })(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');
    /* eslint-enable */

    window.fbq('set', 'autoConfig', false, PIXEL_ID);
    window.fbq('init', PIXEL_ID);
    _loaded = true;
  } catch {
    /* a blocked script must never break the page */
  }
}

/**
 * Honour the visitor's cookie choice. Called by setTrackingConsent().
 *
 * Nothing loads until this is called with `true`. On a later "Decline" the
 * script may already be in the document — there is no unload for it — so the
 * `_granted` guard is what actually enforces the choice, and `fbq` is put into
 * a consent-revoked state as well.
 */
export function setMetaPixelConsent(granted) {
  _granted = Boolean(granted);
  if (!PIXEL_ID) return;
  try {
    if (granted) {
      initMetaPixel();
      if (window.fbq) window.fbq('consent', 'grant');
    } else if (window.fbq) {
      window.fbq('consent', 'revoke');
    }
  } catch { /* ignore */ }
}

/**
 * Forward one app event to Meta, if it's in the map.
 *
 * `eventID` is the deduplication key. The same id is sent again from the
 * server through the Conversions API, and Meta collapses the pair into one
 * conversion — that is the whole point of having both sides. Without it a
 * purchase counts twice and every CPA you read is half what it really is.
 */
export function forwardToMetaPixel(event, props = {}) {
  if (!PIXEL_ID || !_granted || !_loaded) return;
  const mapped = EVENT_MAP[event];
  if (!mapped) return;
  const [metaEvent, buildParams] = mapped;
  try {
    const params = buildParams(props) || {};
    const eventID = props.meta_event_id;
    window.fbq('track', metaEvent, params, eventID ? { eventID } : undefined);
  } catch { /* ignore */ }
}

/**
 * The click identifiers Meta needs to match a conversion back to an ad.
 *
 * `_fbc` and `_fbp` are first-party cookies the pixel writes. When the pixel
 * has not loaded (no id yet, or a blocker), `_fbc` is still reconstructible
 * from `fbclid` in the landing URL using Meta's documented format:
 *   fb.<subdomain-index>.<timestamp-ms>.<fbclid>
 * Reconstructing it is what keeps click attribution working through the
 * server-side path even when the browser pixel is blocked.
 */
export function getMetaClickIds(fbclid) {
  const readCookie = (name) => {
    try {
      const m = document.cookie.match(new RegExp('(^|; )' + name + '=([^;]*)'));
      return m ? decodeURIComponent(m[2]) : null;
    } catch { return null; }
  };
  const fbp = readCookie('_fbp');
  let fbc = readCookie('_fbc');
  if (!fbc && fbclid) fbc = `fb.1.${Date.now()}.${fbclid}`;
  return { fbp, fbc };
}
